import { FC, useState, useEffect } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import { getPropertiesByIdsQuery, RfpPropertyDetails } from '@marriott/mi-groups-graphql';
import { Button, baseVariables } from '@marriott/mi-ui-library';
import { StyledRfpTray, StyledRfpHeader } from './RfpTray.styles';
import { RfpTrayProps, RfpTrayView } from './RfpTray.types';
import { RfpCard } from './RfpCard';
import { CompareHotelsModal } from './CompareHotelsModal';
import { RfpSessionData } from '../EventProfile/EventProfile.types';
import { useLocaleStore, useRfpTrayStore, useSearchResultsStore } from '../../store';
import { Drawer } from '../../molecules/Drawer';
import { RFP_TRAY_MAX_SIZE } from '../../constants';
import { useMediaQuery } from '../../hooks';
import {
  getRfpSessionData,
  updateSession,
  getShortlistedMarshaCodes,
  getRfpProperty,
  getSession,
  getSearchQueryOptions,
  getQueryParams,
  isSearchCriteriaFoundInSession,
} from '../../utils';
import clsx from 'clsx';

export const RfpTrayConfig = {
  emptyLabel: 'RfpTray',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/rfp/rfptray`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RfpTray: FC<{ model: RfpTrayProps }> = (props: any) => {
  const {
    selectedProperties,
    property,
    optional,
    targetUrl,
    undoProperty,
    createRfpCta,
    compare,
    compareHotels,
    removeAriaLabel,
    showAriaLabel,
    hideAriaLabel,
    isSearchResultPage = true,
    createRfpTrackLocation,
  } = props.model;

  const isTabletAndAboveView = useMediaQuery(baseVariables.mediaQuery.md);
  const isDesktop = useMediaQuery(baseVariables.mediaQuery.lg);

  const {
    propertyIds,
    properties,
    visibility,
    setProperties,
    removeProperty,
    markAsRemovedProperty,
    undoMarkAsRemovedProperty,
    setVisibility,
  } = useRfpTrayStore();
  const { searchResults, isQuickGroupEnabled } = useSearchResultsStore();
  const { locale, rtl } = useLocaleStore();

  const [loader, setLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [getPropertiesByIds] = useLazyQuery(getPropertiesByIdsQuery);

  const rfpProperties = Array.from({ length: RFP_TRAY_MAX_SIZE }, (_, index) => {
    if (properties[index]) {
      return properties[index];
    } else {
      return {
        id: '',
        basicInformation: {
          name: '',
        },
        image: {
          defaultImageUrl: '',
          title: '',
          altText: '',
        },
      };
    }
  });

  const isSingleProperty = isSearchResultPage
    ? false
    : properties.filter(property => !property.isMarkForRemoved)?.length === 1;

  const shortlistedMarshaCodes = getShortlistedMarshaCodes(properties);
  const marshaCodes = Object.keys(shortlistedMarshaCodes).join(',');

  useEffect(() => {
    if (isSearchResultPage) {
      populateRfpTray();
    } else {
      if (propertyIds?.length) {
        getRfpProperties(propertyIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyIds]);

  const populateRfpTray = async () => {
    try {
      const { sessionData } = (await getSession()) as RfpSessionData;
      const groupSearchData = sessionData?.cacheData?.data;
      const queryOptions = getSearchQueryOptions(getQueryParams(window.location.search));

      if (groupSearchData && isSearchCriteriaFoundInSession(groupSearchData, queryOptions)) {
        const propertyIds = Object.keys(groupSearchData?.AriesGroupSearch?.rfpShortlistedMarshaCodes || {});
        if (propertyIds?.length) {
          getRfpProperties(propertyIds);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getRfpProperties = (propertyIds: Array<string>) => {
    const uxlOptions: LazyQueryHookOptions = {
      variables: { ids: propertyIds },
      context: {
        headers: {
          'accept-language': locale,
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        const properties = data.propertiesByIds?.map((property: RfpPropertyDetails) =>
          getRfpProperty(property, isDesktop as boolean, isTabletAndAboveView as boolean)
        );
        if (properties?.length) {
          setProperties(properties);
        }
      },
    };

    getPropertiesByIds(uxlOptions);
  };

  const handleRemove = (id: string | number) => {
    isSearchResultPage ? removeProperty(id) : markAsRemovedProperty(id);
  };

  const handleCreateRfp = async () => {
    const rfpSessionData = getRfpSessionData(
      searchResults?.total,
      shortlistedMarshaCodes,
      isQuickGroupEnabled,
      locale
    ) as unknown as Record<string, string>;

    try {
      setLoader(true);
      await updateSession(rfpSessionData);
      setLoader(false);
      window.location.href = `${targetUrl}?locale=${locale.replace(
        '-',
        '_'
      )}&dest=rfp&marshaCode=${marshaCodes}&erfpSignInOverlay=true`;
    } catch (e) {
      setLoader(false);
    }
  };

  const changeRfpTrayView = (isExpanded: boolean) => {
    setVisibility(isExpanded ? RfpTrayView.EXPANDED : RfpTrayView.COLLAPSED);
  };

  const createRfpAction = (
    <Button
      className={clsx('m-button-s', 'rfp-cta', loader && 'm-button skeleton-loader')}
      custom_click_track_value={`${createRfpTrackLocation} | ${createRfpCta} |internal`}
      callback={handleCreateRfp}
    >
      {createRfpCta}
    </Button>
  );

  const compareAction = (
    <Button
      className={clsx('m-button-s', 'rfp-cta', isSingleProperty && 'disabled')}
      isDisabled={isSingleProperty}
      custom_click_track_value={`Compare hotels|${compare} Button|internal|evar106=${marshaCodes.replace(
        /,\s*/g,
        ' '
      )},event24`}
      callback={() => setShowModal(true)}
    >
      {compare}
    </Button>
  );

  const header = (
    <StyledRfpHeader>
      <h2 className="rfp-title">{`${selectedProperties} (${
        isSearchResultPage ? properties.length : properties.filter(property => !property.isMarkForRemoved)?.length
      })`}</h2>
      {isTabletAndAboveView ? (isSearchResultPage ? createRfpAction : compareAction) : null}
    </StyledRfpHeader>
  );

  return (
    <EditableComponent config={RfpTrayConfig} {...props}>
      {properties.length ? (
        <>
          <Drawer
            variant="drawer"
            placement="bottom"
            initialState={visibility === RfpTrayView.EXPANDED}
            title={<div className="container">{header}</div>}
            labels={{
              drawerAriaLabel: selectedProperties,
              hideAriaLabel,
              showAriaLabel,
            }}
            dir={rtl ? 'rtl' : 'ltr'}
            onChange={changeRfpTrayView}
          >
            <StyledRfpTray data-testid="rfptray" data-component-name="o-groups-rfptray">
              <div className="container">
                {header}
                <div className="rfp-properties">
                  {rfpProperties.map((item, index) => (
                    <RfpCard
                      key={index}
                      item={item}
                      index={index}
                      labels={{ property, optional, removeAriaLabel, undoProperty }}
                      isSearchResults={isSearchResultPage}
                      canRemove={!isSingleProperty}
                      onRemove={handleRemove}
                      onUndoRemove={undoMarkAsRemovedProperty}
                    />
                  ))}
                </div>
                {isTabletAndAboveView ? null : isSearchResultPage ? createRfpAction : compareAction}
              </div>
            </StyledRfpTray>
          </Drawer>
          {showModal && (
            <CompareHotelsModal labels={compareHotels} show={showModal} onClose={() => setShowModal(false)} />
          )}
        </>
      ) : null}
    </EditableComponent>
  );
};
